import React, { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../useAuth';
import { usePermissions } from '../hooks/usePermissions';
import { Permission } from '../services/permissions.service';
import Loading from './Loading';

interface PermissionGuardProps {
  children: ReactNode;
  /**
   * Required permissions for this route
   * If user doesn't have any of these permissions, they will be redirected
   */
  requiredPermissions?: Permission[];
  /**
   * Redirect path when user doesn't have permission
   * Defaults to dashboard
   */
  redirectTo?: string;
}

/**
 * Component to guard routes based on user permissions.
 * Waits for userInfo (getMe) to load before checking permissions to avoid redirecting to dashboard on reload when userInfo is still null.
 *
 * @param props - PermissionGuard props
 * @returns Protected route content, Loading (while userInfo loads), or redirect when user lacks permission
 */
export const PermissionGuard: React.FC<PermissionGuardProps> = ({
  children,
  requiredPermissions,
  redirectTo = '/',
}) => {
  const { auth, isUserInfoLoaded } = useAuth();
  const { canAll } = usePermissions();

  // If no permissions required, allow access
  if (!requiredPermissions || requiredPermissions.length === 0) {
    return <>{children}</>;
  }

  // Wait for userInfo (getMe) to load on reload/login before checking permissions; otherwise canAll is false and we redirect to dashboard incorrectly
  if (auth && !isUserInfoLoaded) {
    return <Loading fullScreen />;
  }

  // Check if user has all of the required permissions
  const hasAccess = canAll(requiredPermissions);

  if (!hasAccess) {
    // Redirect to dashboard or specified path
    return <Navigate to={redirectTo} replace />;
  }

  return <>{children}</>;
};
